import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useCloudAuth } from '../../context/CloudAuthContext';
import ActivateService from '../../components/cloud/ActivateService';
import OnboardingWizard from '../../components/cloud/OnboardingWizard';

const tabs = [
  { name: 'Overview', href: '/cloud/voice' },
  { name: 'Calls', href: '/cloud/voice/calls' },
  { name: 'Analytics', href: '/cloud/voice/analytics' },
  { name: 'Prompts', href: '/cloud/voice/prompts' },
  { name: 'Settings', href: '/cloud/voice/settings' },
  { name: 'Billing', href: '/cloud/voice/billing' },
];

export default function VoicePage() {
  const { fetchApi } = useCloudAuth();
  const location = useLocation();
  const [status, setStatus] = useState('loading'); // loading | inactive | onboarding | active
  const [onboardingStep, setOnboardingStep] = useState(1);

  useEffect(() => {
    loadServiceStatus();
  }, []);

  const loadServiceStatus = async () => {
    try {
      const res = await fetchApi('/api/cloud/auth/services/voice');
      const service = res.data;
      
      if (service.status === 'inactive') {
        setStatus('inactive');
      } else if (service.status === 'active' && !service.onboarding.completed) {
        setStatus('onboarding');
        setOnboardingStep(service.onboarding.step || 1);
      } else {
        setStatus('active');
      }
    } catch (err) {
      console.error('Failed to load service status:', err);
      setStatus('inactive');
    }
  };

  const handleActivate = () => {
    setStatus('onboarding');
    setOnboardingStep(1);
  };

  const handleOnboardingComplete = () => {
    setStatus('active');
  };

  if (status === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <svg className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'inactive') {
    return <ActivateService onActivate={handleActivate} />;
  }

  if (status === 'onboarding') {
    return (
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-white">Voice Agent Setup</h1>
          <p className="text-gray-400 mt-1">Complete these steps to get started</p>
        </div>
        <OnboardingWizard
          currentStep={onboardingStep}
          onComplete={handleOnboardingComplete}
          onStepChange={setOnboardingStep}
        />
      </div>
    );
  }

  // Active - show dashboard
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-800">
        <div className="px-8 py-6">
          <h1 className="text-2xl font-semibold text-white">Voice Agent</h1>
          <p className="text-gray-400 mt-1">Manage your AI voice agents and calls</p>
        </div>

        {/* Tabs */}
        <div className="px-8">
          <nav className="flex gap-1 -mb-px">
            {tabs.map((tab) => {
              const isActive = location.pathname === tab.href || 
                (tab.href === '/cloud/voice' && location.pathname === '/cloud/voice');
              return (
                <NavLink
                  key={tab.name}
                  to={tab.disabled ? '#' : tab.href}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'border-purple-500 text-purple-400'
                      : tab.disabled
                      ? 'border-transparent text-gray-600 cursor-not-allowed'
                      : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
                  }`}
                  onClick={tab.disabled ? (e) => e.preventDefault() : undefined}
                  end={tab.href === '/cloud/voice'}
                >
                  {tab.name}
                  {tab.disabled && <span className="ml-2 text-xs text-gray-600">(Soon)</span>}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        <Outlet />
      </div>
    </div>
  );
}
